import { filter } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { usePowerOnOffVisibility } from '@/comp/MapBuilder/nodes/nodeWidget/DetailsDrawer/Footer/StartStopButtons/StartStopButtons.utils';
import { checkTierPermission } from '@/utils/util-functions';
import { AlertStatus, ComponentTierName } from '@/constants/enums';
import { DeviceType } from '@/constants/deviceTypeEnum';

import { getPoweredOnState } from '@/comp/StationControlCenter/SccUtils';
import { SCCUniversalDevice } from '@/comp/StationControlCenter/types/universal-device.types';
import { getDeviceIconAndLabel, getLabelByKey } from '../UniversalDeviceWidget.utils';
import { MetricVM } from '../components/Metrics/MetricItem/MetricItem.types';
import { MetricType } from '@/constants/metric-type.enum';

export function useUniversalDeviceWidgetVM(deviceSCC: SCCUniversalDevice) {
  const { t } = useTranslation();
  const { tunit, tvalue, measurementSystem } = useMeasurement();
  const { stationStore, alertStore } = useStores();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { icon, label } = getDeviceIconAndLabel(deviceSCC.deviceType);
  const { poweredOnMetricPresent } = usePowerOnOffVisibility(deviceSCC.id);

  const poweredOn = getPoweredOnState(deviceSCC.poweredOn);
  const hasBar = deviceSCC.realtimeMetrics?.some(metric => metric.type === 'slider') ?? false;

  const nonSliderMetrics = deviceSCC.realtimeMetrics?.filter(metric => metric.type !== 'slider') ?? [];
  const visibleNonSliderMetrics =
    deviceSCC.deviceType === DeviceType.General ? nonSliderMetrics.slice(0, 3) : nonSliderMetrics;

  const getMetricLabel = (metricKey: string, isForFooter = false) => {
    if (metricKey === MetricType.HorsePower) {
      return measurementSystem === 'IMPERIAL' ? t('RATED_HP') : t('RATED_KW');
    }

    // Use shorter translation for Temperature in footer
    if (isForFooter && metricKey === MetricType.TempCelsius) {
      return t('Temp'); 
    }

    // For regular content, use TEMPERATURE key for full translation
    if (!isForFooter && metricKey === MetricType.TempCelsius) {
      return t('TEMPERATURE');
    }

    return t(getLabelByKey(metricKey));
  };

  const metrics: MetricVM[] = visibleNonSliderMetrics.map((metric, index) => {
    const isHorsePower = metric.key === MetricType.HorsePower;

    const value = isHorsePower
      ? tvalue(metric.value, metric.unit, measurementSystem, 'IMPERIAL')
      : tvalue(metric.value, metric.unit);

    const unit = isHorsePower ? undefined : t(tunit(metric.unit));

    return {
      id: `${String(metric.key)}-${index}`,
      label: getMetricLabel(metric.key),
      value,
      unit,
      metricKey: metric.key,
    };
  });

  const alertsByDeviceId = alertStore.filterAlertsByDeviceId(deviceSCC.id);
  const activeAlerts = alertStore.filterActiveAlerts(alertsByDeviceId);
  const activeOpenAlerts = filter(activeAlerts, { status: AlertStatus.Open }) || [];

  const isPermissionStationControlView = checkTierPermission(ComponentTierName.StationControlCenterView, stationStore);
  const shouldDisplayPoweredOnIndication = poweredOnMetricPresent && deviceSCC.poweredOn != null;

  const currentAsset = stationStore.assetsDevices?.filter(deviceItem => deviceItem.deviceId === deviceSCC.id)[0];

  const inMaintenanceAction = deviceSCC.actions?.find(action => action.key === 'inMaintenance');
  const showMaintenanceToggle = Boolean(inMaintenanceAction);
  const inMaintenance = inMaintenanceAction?.value ?? false;

  return {
    poweredOn,
    drawer: {
      isOpen: isDrawerOpen,
      open: () => setIsDrawerOpen(true),
      close: () => setIsDrawerOpen(false),
    },
    header: {
      icon,
      label,
      device: deviceSCC,
      currentAsset,
    },
    content: {
      hasBar,
      device: deviceSCC,
      isPermissionStationControlView,
      metrics,
      activeOpenAlerts,
    },
    footer: {
      metrics: hasBar
        ? metrics.map(metric => ({
            ...metric,
            label:
              metric.metricKey === MetricType.TempCelsius
                ? getMetricLabel(metric.metricKey, true)
                : metric.label,
          }))
        : undefined,
      device: deviceSCC,
      shouldDisplayPoweredOnIndication,
      showMaintenanceToggle,
      inMaintenance,
    },
  };
}
